/**
 * ScanTab — ERP Workspace "Scan (Local)" tab.
 * Full Local Component Scanner hub:
 *   1. Download erp-scanner.js to run on your ERP project
 *   2. Import resulting JSON → staged review with confidence badges → bulk accept
 *   3. Generate code snippets (menu.config.js + screens/index.js)
 *   ── Server Auto-Scan (scans this admin app's frontend) at the bottom ──
 */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Download, Upload, Terminal, Copy, Check, RefreshCw,
  ChevronDown, ChevronUp, Cpu, Zap, Code2, X,
  FileCode, CheckCircle2, AlertTriangle, BarChart3, Sparkles,
  Search, ArrowRight, Layers3,
} from 'lucide-react';
import { API, authFetch } from '../../lib/api';
import { erpToast } from '../../components/erp/ERPToast';

// ── Confidence badge ─────────────────────────────────────────────────────────
function ConfidenceBadge({ score }) {
  const s = Number(score) || 0;
  const { color, bg, label } =
    s >= 90 ? { color: '#15803d', bg: 'rgba(34,197,94,.14)', label: 'Excellent' } :
    s >= 80 ? { color: '#16a34a', bg: 'rgba(34,197,94,.1)',  label: 'High'      } :
    s >= 65 ? { color: '#2563eb', bg: 'rgba(59,130,246,.1)', label: 'Good'      } :
    s >= 50 ? { color: '#b45309', bg: 'rgba(245,158,11,.1)', label: 'Fair'      } :
              { color: '#b91c1c', bg: 'rgba(239,68,68,.1)',  label: 'Low'       };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      fontSize: '0.65rem', fontWeight: 700, padding: '2px 6px',
      borderRadius: 5, color, background: bg, whiteSpace: 'nowrap',
    }}>
      <BarChart3 size={9} />
      {s}% {label}
    </span>
  );
}

// ── Code Snippets Modal ───────────────────────────────────────────────────────
function CodeSnippetsModal({ onClose }) {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('menu');
  const [copied,   setCopied]   = useState(null);

  React.useEffect(() => {
    authFetch(`${API}/erp-workspace/admin/code-snippets`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { erpToast.error(e.message); setLoading(false); });
  }, []);

  const code = tab === 'menu' ? data?.menu : data?.barrel;

  const copy = () => {
    navigator.clipboard?.writeText(code || '').then(() => {
      setCopied(tab); setTimeout(() => setCopied(null), 1800);
    });
  };

  const download = () => {
    const fname = tab === 'menu' ? 'menu.config.js' : 'screens_index.js';
    const blob = new Blob([code || ''], { type: 'text/javascript' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url; a.download = fname; a.click();
    URL.revokeObjectURL(url);
  };

  const tabStyle = (id) => ({
    padding: '6px 14px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
    border: 'none', background: 'none',
    borderBottom: tab === id ? '2px solid #3b82f6' : '2px solid transparent',
    color: tab === id ? '#3b82f6' : 'var(--text-secondary)',
    marginBottom: '-1px',
  });

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', zIndex:9999,
      display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'var(--surface,#fff)', borderRadius:12, width:720, maxWidth:'95vw',
        maxHeight:'85vh', display:'flex', flexDirection:'column',
        boxShadow:'0 24px 64px rgba(0,0,0,.28)' }}>

        {/* Header */}
        <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)',
          display:'flex', alignItems:'center', gap:8 }}>
          <Code2 size={16} color="#3b82f6" />
          <span style={{ fontWeight:700, fontSize:'0.95rem' }}>Code Snippets</span>
          {!loading && (
            <span style={{ fontSize:'0.72rem', color:'#64748b', background:'var(--surface-2,#f1f5f9)',
              padding:'1px 8px', borderRadius:10 }}>
              {data?.total || 0} mapped screens · {new Date(data?.generatedAt || 0).toLocaleString()}
            </span>
          )}
          <div style={{ flex:1 }} />
          <button onClick={onClose}
            style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', padding:4 }}>
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ padding:'6px 20px 0', borderBottom:'1px solid var(--border)',
          display:'flex', gap:2 }}>
          <button style={tabStyle('menu')} onClick={() => setTab('menu')}>
            <Code2 size={11} style={{ verticalAlign:'middle', marginRight:4 }} />menu.config.js
          </button>
          <button style={tabStyle('barrel')} onClick={() => setTab('barrel')}>
            <FileCode size={11} style={{ verticalAlign:'middle', marginRight:4 }} />screens/index.js
          </button>
        </div>

        {/* Description */}
        <div style={{ padding:'8px 20px 0', fontSize:'0.73rem', color:'#64748b', lineHeight:1.5 }}>
          {tab === 'menu'
            ? 'Full module → sub-module → screen navigation tree. Drop into your ERP app as the sidebar/router config.'
            : 'Lazy-import registry for all mapped screens. Use React.lazy() to dynamically load any screen by its UAMS screen ID.'}
        </div>

        {/* Code area */}
        <div style={{ flex:1, overflow:'auto', padding:'10px 20px' }}>
          {loading ? (
            <div style={{ textAlign:'center', padding:40, color:'#94a3b8' }}>
              <RefreshCw size={16} className="spin" style={{ verticalAlign:'middle', marginRight:6 }} />
              Generating snippets…
            </div>
          ) : (
            <pre style={{
              margin:0, fontFamily:'monospace', fontSize:'0.71rem', lineHeight:1.6,
              color:'var(--text-primary,#1e293b)', whiteSpace:'pre-wrap', wordBreak:'break-word',
              background:'var(--surface-2,#f8fafc)', padding:'12px 14px',
              borderRadius:8, border:'1px solid var(--border)', overflow:'auto',
              maxHeight:'42vh',
            }}>
              {code || '// No mapped screens yet — map components first in the Component Map tab.'}
            </pre>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'10px 20px', borderTop:'1px solid var(--border)',
          display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button className="erp-btn" onClick={copy}
            data-testid="snippets-copy-btn"
            style={{ fontSize:'0.75rem', display:'flex', alignItems:'center', gap:4 }}>
            {copied === tab
              ? <><Check size={12} color="#22c55e" /> Copied!</>
              : <><Copy size={12} /> Copy</>}
          </button>
          <button className="erp-btn erp-btn-primary" onClick={download}
            data-testid="snippets-download-btn"
            style={{ fontSize:'0.75rem', display:'flex', alignItems:'center', gap:4 }}>
            <Download size={12} />
            Download {tab === 'menu' ? 'menu.config.js' : 'screens_index.js'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Snippet preview tooltip (hover on staged row) ────────────────────────────
function SnippetTooltip({ item, pos }) {
  if (!item) return null;
  const cp    = item.componentPath || '';
  const name  = item.componentName || cp.split('/').pop().replace(/\.(jsx?|tsx?)$/, '') || 'Component';
  const clean = cp.replace(/\.(jsx?|tsx?)$/, '');
  const lines = [
    `// ${item._screenTitle || item.screenId || 'Screen'}`,
    `import ${name} from './${clean || name}';`,
    ``,
    `// lazy variant:`,
    `const ${name} = React.lazy(() => import('./${clean || name}'));`,
  ].join('\n');

  return createPortal(
    <div style={{
      position: 'fixed', top: pos.y, left: pos.x, zIndex: 10000,
      background: '#1e293b', color: '#e2e8f0',
      borderRadius: 8, padding: '10px 14px',
      fontFamily: 'monospace', fontSize: '0.72rem', lineHeight: 1.65,
      maxWidth: 440, minWidth: 280,
      boxShadow: '0 10px 32px rgba(0,0,0,.35)',
      border: '1px solid rgba(99,102,241,.35)',
      pointerEvents: 'none', whiteSpace: 'pre',
    }}>
      <div style={{ fontSize: '0.63rem', color: '#7c87a6', marginBottom: 6,
        textTransform: 'uppercase', letterSpacing: '.04em', fontFamily: 'sans-serif' }}>
        Import Snippet Preview
      </div>
      <span style={{ color: '#7dd3fc' }}>{'// '}</span>
      <span style={{ color: '#94a3b8' }}>{item._screenTitle || item.screenId || 'Screen'}{'\n'}</span>
      <span style={{ color: '#c084fc' }}>{'import '}</span>
      <span style={{ color: '#fde68a' }}>{name}</span>
      <span style={{ color: '#e2e8f0' }}>{' from '}</span>
      <span style={{ color: '#86efac' }}>{`'./${clean || name}'`}</span>
      <span style={{ color: '#e2e8f0' }}>{';\n'}</span>
      <span style={{ color: '#7dd3fc' }}>{'// lazy:  '}</span>
      <span style={{ color: '#c084fc' }}>{'React.lazy'}</span>
      <span style={{ color: '#e2e8f0' }}>{`(() => import('./${clean || name}'))`}</span>
    </div>,
    document.body
  );
}

// ── Main ScanTab ─────────────────────────────────────────────────────────────
export function ScanTab({ onNavigate, onAcceptComplete }) {
  // ── Step 1: Download Script ─────────────────────────────────────────────
  const [minScore,    setMinScore]    = useState(70);
  const [cmdCopied,   setCmdCopied]   = useState(false);
  const [downloading, setDownloading] = useState(false);

  // ── Step 2: Staged Import ───────────────────────────────────────────────
  const [stagedItems,  setStagedItems]  = useState([]);
  const [selectedIds,  setSelectedIds]  = useState(new Set());
  const [confFilter,   setConfFilter]   = useState('all');
  const [accepting,    setAccepting]    = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importMeta,   setImportMeta]   = useState(null);
  const [searchQ,      setSearchQ]      = useState('');
  const fileRef = useRef();

  // ── Batch progress ──────────────────────────────────────────────────────
  const [importProgress, setImportProgress] = useState(null); // {done,total,batch,batches} | null

  // ── Import history (session) ────────────────────────────────────────────
  const [importHistory, setImportHistory] = useState([]);
  const [historyOpen,   setHistoryOpen]   = useState(false);

  // ── Staged table pagination ─────────────────────────────────────────────
  const [stagedPage,   setStagedPage]   = useState(1);
  const STAGED_PAGE_SIZE = 50;

  // ── Post-import state ───────────────────────────────────────────────────
  const [postImportCounts, setPostImportCounts] = useState(null);  // moduleUnmappedCounts after accept
  const [navIn,            setNavIn]            = useState(null);   // countdown seconds before nav

  // ── Hover snippet tooltip ───────────────────────────────────────────────
  const [hoveredItem, setHoveredItem] = useState(null);
  const [tooltipPos,  setTooltipPos]  = useState({ x: 0, y: 0 });

  // ── Countdown → navigate to Component Map ──────────────────────────────
  useEffect(() => {
    if (navIn === null) return;
    if (navIn <= 0) { setNavIn(null); onNavigate?.('compmap'); return; }
    const t = setTimeout(() => setNavIn(n => (n > 0 ? n - 1 : null)), 1000);
    return () => clearTimeout(t);
  }, [navIn, onNavigate]);

  // ── Step 3: Code Snippets ───────────────────────────────────────────────
  const [showSnippets, setShowSnippets] = useState(false);

  // ── Server Auto-Scan (existing) ─────────────────────────────────────────
  const [autoOpen,     setAutoOpen]     = useState(false);
  const [scanResult,   setScanResult]   = useState(null);
  const [scanning,     setScanning]     = useState(false);
  const [applying,     setApplying]     = useState(new Set());
  const [bulkApplying, setBulkApplying] = useState(false);
  const [autoSearch,   setAutoSearch]   = useState('');
  const [autoFilter,   setAutoFilter]   = useState('');

  // ── Derived CLI command ─────────────────────────────────────────────────
  const CMD = minScore !== 70
    ? `node erp-scanner.js --src ./src --min-score ${minScore}`
    : `node erp-scanner.js --src ./src`;

  // ── Download scanner script ─────────────────────────────────────────────
  const downloadScript = async () => {
    setDownloading(true);
    try {
      const r = await authFetch(`${API}/erp-workspace/admin/scanner-script`);
      if (!r.ok) { erpToast.error('Failed to generate script'); setDownloading(false); return; }
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a'); a.href = url; a.download = 'erp-scanner.js'; a.click();
      URL.revokeObjectURL(url);
      erpToast.success('erp-scanner.js downloaded — drop it into your ERP project root');
    } catch (e) { erpToast.error(e.message); }
    setDownloading(false);
  };

  const copyCmd = () => {
    navigator.clipboard?.writeText(CMD).then(() => {
      setCmdCopied(true); setTimeout(() => setCmdCopied(false), 1800);
    });
  };

  // ── Parse file locally (no server call yet) ─────────────────────────────
  const parseFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const screens = Array.isArray(parsed) ? parsed : (parsed.screens || []);
      const items = screens.filter(s => s.componentName || s.componentPath);
      setImportMeta(parsed._meta || null);
      setStagedItems(items);
      setImportResult(null);
      setConfFilter('all');
      setSearchQ('');
      // Pre-select high confidence (>= 80%)
      const highIds = new Set(
        items.filter(s => (s._confidence || 0) >= 80 && s.screenId).map(s => String(s.screenId))
      );
      setSelectedIds(highIds);
      erpToast.success(`${items.length} screens loaded — review below before applying`);
    } catch (ex) { erpToast.error('Invalid JSON: ' + ex.message); }
  };

  // ── Filter staged items ─────────────────────────────────────────────────
  const filteredItems = useMemo(() => {
    return stagedItems.filter(s => {
      const c = s._confidence || 0;
      if (confFilter === 'high'   && c < 80)           return false;
      if (confFilter === 'medium' && (c < 50 || c >= 80)) return false;
      if (confFilter === 'low'    && c >= 50)           return false;
      if (searchQ) {
        const q = searchQ.toLowerCase();
        return (s.screenId || '').toLowerCase().includes(q) ||
          (s._screenTitle || '').toLowerCase().includes(q) ||
          (s.componentName || '').toLowerCase().includes(q) ||
          (s.componentPath || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [stagedItems, confFilter, searchQ]);

  const highCount   = useMemo(() => stagedItems.filter(s => (s._confidence || 0) >= 80).length, [stagedItems]);
  const medCount    = useMemo(() => stagedItems.filter(s => { const c=s._confidence||0; return c>=50&&c<80; }).length, [stagedItems]);
  const lowCount    = useMemo(() => stagedItems.filter(s => (s._confidence || 0) < 50).length, [stagedItems]);

  // Reset pagination when filter/search changes
  useEffect(() => { setStagedPage(1); }, [confFilter, searchQ]);

  const toggleRow = (id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const selectAll = (items) => setSelectedIds(new Set(items.map(s => String(s.screenId)).filter(Boolean)));
  const selectNone = () => setSelectedIds(new Set());
  const selectHigh = () => setSelectedIds(new Set(
    stagedItems.filter(s => (s._confidence||0) >= 80 && s.screenId).map(s => String(s.screenId))
  ));

  // ── Accept selected staged items (chunked for progress feedback) ─────────
  const IMPORT_CHUNK = 100; // frontend chunks for progress; backend batches at 400
  const acceptSelected = async (itemsToSend) => {
    if (!itemsToSend.length) { erpToast.error('No items selected'); return; }
    setAccepting(true);
    setImportProgress(null);

    // Deduplicate by screenId on the frontend too (safety)
    const seenFe = new Map();
    for (const s of itemsToSend) {
      const key = String(s.screenId || '');
      if (!seenFe.has(key) || (s._confidence||0) > (seenFe.get(key)._confidence||0)) seenFe.set(key, s);
    }
    const deduped = Array.from(seenFe.values());

    // Build chunks
    const chunks = [];
    for (let i = 0; i < deduped.length; i += IMPORT_CHUNK) chunks.push(deduped.slice(i, i + IMPORT_CHUNK));
    const showProgress = deduped.length >= 200;

    let totalDirect = 0, totalMatched = 0, totalFailed = 0, totalApplied = 0;
    let allOk = true;

    for (let ci = 0; ci < chunks.length; ci++) {
      if (showProgress) {
        setImportProgress({ done: ci * IMPORT_CHUNK, total: deduped.length, batch: ci + 1, batches: chunks.length });
      }
      const chunk = chunks[ci];
      const payload = chunk.map(s => ({
        screenId: s.screenId, componentName: s.componentName,
        componentPath: s.componentPath, isImplemented: s.isImplemented || false,
      }));
      try {
        const r = await authFetch(`${API}/erp-workspace/admin/import-manifest`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ screens: payload }),
        });
        const d = await r.json();
        if (d.error) { erpToast.error(d.error); allOk = false; break; }
        totalDirect  += d.direct  || 0;
        totalMatched += d.matched || 0;
        totalFailed  += d.failed  || 0;
        totalApplied += d.appliedTotal || 0;
      } catch (ex) { erpToast.error(ex.message); allOk = false; break; }
    }

    setImportProgress(null);
    if (allOk) {
      const result = { direct: totalDirect, matched: totalMatched, failed: totalFailed, appliedTotal: totalApplied, total: deduped.length };
      setImportResult(result);
      setStagedItems([]);
      setSelectedIds(new Set());
      erpToast.success(`Applied ${totalApplied} screens — ${totalDirect} direct + ${totalMatched} fuzzy matched`);

      // Add to import history
      const acceptedIds = deduped.filter(s => s.screenId).map(s => String(s.screenId));
      setImportHistory(prev => [{
        timestamp:   new Date().toISOString(),
        srcDir:      importMeta?.srcDir || 'erp-mapping.json',
        total:       deduped.length,
        applied:     totalApplied,
        failed:      totalFailed,
        direct:      totalDirect,
        matched:     totalMatched,
      }, ...prev]);

      // Notify parent with accepted IDs (for flash highlight in ComponentMap)
      onAcceptComplete?.(acceptedIds);

      // Fetch fresh unmapped counts
      authFetch(`${API}/erp-workspace/admin/stats`)
        .then(res => res.json())
        .then(stats => setPostImportCounts(stats.moduleUnmappedCounts || []))
        .catch(() => {});
      // Start countdown → auto-navigate to Component Map
      setNavIn(5);
    }
    setAccepting(false);
  };

  const acceptHigh = () => {
    const items = stagedItems.filter(s => (s._confidence||0) >= 80 && s.screenId);
    acceptSelected(items);
  };

  const acceptAllSelected = () => {
    const items = stagedItems.filter(s => s.screenId && selectedIds.has(String(s.screenId)));
    acceptSelected(items);
  };

  // ── Server Auto-Scan (existing) ─────────────────────────────────────────
  const runAutoScan = async () => {
    setScanning(true);
    try {
      const r = await authFetch(`${API}/erp-workspace/admin/scan-components`);
      setScanResult(await r.json());
    } catch (e) { erpToast.error(e.message); }
    setScanning(false);
  };

  const applyAutoSuggestion = async (s) => {
    setApplying(a => new Set(a).add(s.screenId));
    try {
      const r = await authFetch(`${API}/erp-workspace/admin/component-map/${s.screenId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ componentName: s.suggestedName, componentPath: s.suggestedPath, isImplemented: false }),
      });
      const d = await r.json();
      if (d.ok) {
        setScanResult(prev => ({
          ...prev,
          suggestions: prev.suggestions.map(x => x.screenId === s.screenId
            ? { ...x, currentComponent: s.suggestedName, confidence: 'applied' } : x),
        }));
        erpToast.success(`Applied: ${s.suggestedName}`);
      } else { erpToast.error(d.error); }
    } catch (e) { erpToast.error(e.message); }
    setApplying(a => { const n = new Set(a); n.delete(s.screenId); return n; });
  };

  const applyAutoBulk = async (minConf) => {
    if (!scanResult) return;
    const confOrder = { high: 2, medium: 1, none: 0 };
    const minScore2 = confOrder[minConf] ?? 1;
    const toApply = scanResult.suggestions.filter(s =>
      s.suggestedName && s.confidence !== 'applied' && (confOrder[s.confidence] ?? 0) >= minScore2
    );
    if (!toApply.length) { erpToast.info('No matching suggestions'); return; }
    setBulkApplying(true);
    try {
      const r = await authFetch(`${API}/erp-workspace/admin/scan-apply-bulk`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ suggestions: toApply }),
      });
      const d = await r.json();
      if (d.ok) {
        const ids = new Set(toApply.map(s => s.screenId));
        setScanResult(prev => ({
          ...prev,
          suggestions: prev.suggestions.map(x => ids.has(x.screenId)
            ? { ...x, currentComponent: x.suggestedName, confidence: 'applied' } : x),
        }));
        erpToast.success(`Bulk applied ${d.applied}`);
      } else { erpToast.error(d.error); }
    } catch (e) { erpToast.error(e.message); }
    setBulkApplying(false);
  };

  const autoFiltered = useMemo(() => {
    if (!scanResult) return [];
    const q = autoSearch.toLowerCase();
    return scanResult.suggestions.filter(s => {
      if (autoFilter && s.confidence !== autoFilter) return false;
      return !q || (s.screenId||'').toLowerCase().includes(q) ||
        (s.screenTitle||'').toLowerCase().includes(q) || (s.suggestedName||'').toLowerCase().includes(q);
    });
  }, [scanResult, autoSearch, autoFilter]);

  // ── UI helpers ──────────────────────────────────────────────────────────
  const th = { padding:'6px 10px', textAlign:'left', fontWeight:700, color:'#64748b',
    fontSize:'0.67rem', textTransform:'uppercase', letterSpacing:'.04em',
    background:'var(--surface-2,#f8fafc)', whiteSpace:'nowrap' };

  const pillBtn = (active) => ({
    padding:'3px 11px', fontSize:'0.73rem', fontWeight:700, borderRadius:20,
    cursor:'pointer', border:'none', transition:'all .12s',
    background: active ? '#3b82f6' : 'var(--surface,#fff)',
    color: active ? '#fff' : 'var(--text-secondary)',
    boxShadow: active ? 'none' : '0 0 0 1px var(--border)',
  });

  const stepHead = (num, icon, title, sub) => (
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
      <div style={{
        width:28, height:28, borderRadius:'50%',
        background:'linear-gradient(135deg,#3b82f6,#8b5cf6)',
        color:'#fff', fontSize:'0.75rem', fontWeight:900,
        display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
      }}>{num}</div>
      <div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          {icon}
          <span style={{ fontWeight:800, fontSize:'0.88rem', color:'var(--text-primary,#1e293b)' }}>{title}</span>
        </div>
        {sub && <div style={{ fontSize:'0.72rem', color:'#64748b', marginTop:1 }}>{sub}</div>}
      </div>
    </div>
  );

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={{ padding:20, display:'flex', flexDirection:'column', gap:20, maxWidth:1100 }}>

      {/* ── Page Header ─────────────────────────────────────────── */}
      <div style={{ display:'flex', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
            <Sparkles size={18} color="#3b82f6" />
            <h2 style={{ margin:0, fontSize:'1.1rem', fontWeight:800, color:'var(--text-primary,#1e293b)' }}>
              Local Component Scanner
            </h2>
            <span style={{ fontSize:'0.68rem', fontWeight:700, color:'#3b82f6',
              background:'rgba(59,130,246,.1)', padding:'2px 8px', borderRadius:10 }}>
              v1.0
            </span>
          </div>
          <p style={{ margin:0, fontSize:'0.78rem', color:'#64748b', lineHeight:1.5 }}>
            Download <code style={{ background:'var(--surface-2)', padding:'1px 4px', borderRadius:3 }}>erp-scanner.js</code>,
            run it on your ERP project to auto-map React components to UAMS screen IDs, then import the result here.
          </p>
        </div>
        <button
          className="erp-btn erp-btn-primary"
          onClick={() => setShowSnippets(true)}
          data-testid="scantab-snippets-btn"
          style={{ display:'flex', alignItems:'center', gap:5, fontSize:'0.78rem', whiteSpace:'nowrap' }}>
          <Code2 size={13} /> Code Snippets
        </button>
      </div>

      {/* ── STEP 1: Download Script ─────────────────────────────── */}
      <div style={{ background:'var(--surface,#fff)', border:'1px solid var(--border)',
        borderRadius:10, padding:'18px 20px' }}>
        {stepHead(1, <Download size={14} color="#3b82f6" />, 'Download Scanner Script',
          'Get erp-scanner.js — a Node.js script pre-loaded with all ' +
          '821 UAMS screens. Drop it into your ERP project root.')}

        {/* Threshold presets */}
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, flexWrap:'wrap' }}>
          <span style={{ fontSize:'0.72rem', color:'#64748b', fontWeight:600 }}>Confidence threshold:</span>
          {[
            { score:55, label:'Low (55%)',     desc:'Catches more, more false positives' },
            { score:70, label:'Default (70%)', desc:'Balanced — recommended'             },
            { score:85, label:'Strict (85%)',  desc:'Only very strong matches'           },
          ].map(p => (
            <button key={p.score}
              title={p.desc}
              onClick={() => setMinScore(p.score)}
              style={{
                ...pillBtn(minScore === p.score),
                position:'relative',
              }}
              data-testid={`scantab-preset-${p.score}`}>
              {p.label}
              {minScore === p.score && (
                <span style={{ marginLeft:4, fontSize:'0.6rem' }}>✓</span>
              )}
            </button>
          ))}
        </div>

        {/* CLI command */}
        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:14 }}>
          <div style={{
            flex:1, fontFamily:'monospace', fontSize:'0.78rem', padding:'7px 12px',
            background:'var(--surface-2,#f8fafc)', border:'1px solid var(--border)',
            borderRadius:6, color:'#1e293b', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          }}>
            <span style={{ color:'#94a3b8', marginRight:6 }}>$</span>{CMD}
          </div>
          <button onClick={copyCmd}
            data-testid="scantab-copy-cmd"
            style={{ padding:'7px 12px', fontSize:'0.73rem', border:'1px solid var(--border)',
              borderRadius:6, cursor:'pointer', background:'var(--surface)',
              display:'flex', alignItems:'center', gap:4,
              color: cmdCopied ? '#22c55e' : '#64748b' }}>
            {cmdCopied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
          </button>
        </div>

        {/* Download button */}
        <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
          <button className="erp-btn erp-btn-primary" onClick={downloadScript}
            disabled={downloading}
            data-testid="scantab-download-btn"
            style={{ fontSize:'0.83rem', padding:'8px 18px', display:'flex', alignItems:'center', gap:6,
              background:'#3b82f6', borderColor:'#3b82f6' }}>
            {downloading
              ? <><RefreshCw size={13} className="spin" /> Generating…</>
              : <><Download size={13} /> Download erp-scanner.js</>}
          </button>
          <div style={{ fontSize:'0.72rem', color:'#94a3b8', display:'flex', alignItems:'center', gap:4 }}>
            <Terminal size={11} />
            Works with React (.jsx/.tsx/.js) · skips test/spec files automatically
          </div>
        </div>
      </div>

      {/* ── STEP 2: Import Scanner Output ──────────────────────── */}
      <div style={{ background:'var(--surface,#fff)', border:'1px solid var(--border)',
        borderRadius:10, padding:'18px 20px' }}>
        {stepHead(2, <Upload size={14} color="#8b5cf6" />, 'Import Scanner Output',
          'Upload erp-mapping.json — review confidence scores, then bulk-accept to map components.')}

        {/* Drop zone */}
        {!stagedItems.length && !importResult && (
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#3b82f6'; }}
            onDragLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; }}
            onDrop={e => {
              e.preventDefault(); e.currentTarget.style.borderColor = '#cbd5e1';
              const f = e.dataTransfer.files?.[0];
              if (f) { const dt = new DataTransfer(); dt.items.add(f); fileRef.current.files = dt.files; parseFile({ target: fileRef.current }); }
            }}
            data-testid="scantab-dropzone"
            style={{
              border:'2px dashed #cbd5e1', borderRadius:8, padding:'28px 20px',
              textAlign:'center', cursor:'pointer', transition:'border-color .15s',
            }}>
            <Upload size={24} color="#94a3b8" style={{ marginBottom:8 }} />
            <div style={{ fontSize:'0.82rem', fontWeight:600, color:'var(--text-secondary)', marginBottom:4 }}>
              Drop erp-mapping.json here or click to browse
            </div>
            <div style={{ fontSize:'0.72rem', color:'#94a3b8' }}>
              Generated by <code>node erp-scanner.js</code> in your ERP project
            </div>
          </div>
        )}
        <input ref={fileRef} type="file" accept=".json" style={{ display:'none' }} onChange={parseFile} />

        {/* Import success result */}
        {importResult && !stagedItems.length && (
          <div style={{ borderRadius:8, overflow:'hidden',
            border:'1px solid rgba(34,197,94,.22)' }}>
            {/* Green success bar */}
            <div style={{ background:'rgba(34,197,94,.07)', padding:'10px 16px',
              display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
              <CheckCircle2 size={16} color="#22c55e" />
              <span style={{ fontSize:'0.82rem', fontWeight:700, color:'#15803d' }}>
                Import complete — {importResult.appliedTotal} screens applied
              </span>
              <span style={{ fontSize:'0.73rem', color:'#64748b' }}>
                {importResult.direct} direct &middot; {importResult.matched} fuzzy &middot;
                {importResult.unmatched} unmatched &middot; {importResult.failed} failed
              </span>
              <div style={{ flex:1 }} />
              <button onClick={() => { setImportResult(null); setPostImportCounts(null); setNavIn(null); }}
                style={{ background:'none', border:'1px solid #cbd5e1', borderRadius:5,
                  padding:'3px 8px', cursor:'pointer', fontSize:'0.72rem', color:'#64748b' }}>
                Import another file
              </button>
            </div>

            {/* Unmapped-by-module breakdown */}
            {postImportCounts && postImportCounts.length > 0 && (
              <div style={{ background:'var(--surface-2,#f8fafc)', padding:'10px 16px',
                borderTop:'1px solid rgba(34,197,94,.15)' }}>
                <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#64748b',
                  marginBottom:8, display:'flex', alignItems:'center', gap:5 }}>
                  <Layers3 size={11} />
                  Remaining unmapped screens by module
                  <span style={{ fontWeight:400, color:'#94a3b8' }}>
                    — ({postImportCounts.reduce((s, m) => s + m.unmapped, 0)} total)
                  </span>
                </div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:'4px 10px' }}>
                  {postImportCounts.slice(0, 12).map(m => (
                    <span key={m.ModuleId} style={{
                      fontSize:'0.69rem', color:'#475569',
                      display:'inline-flex', alignItems:'center', gap:4,
                      background:'var(--surface,#fff)', border:'1px solid var(--border)',
                      borderRadius:5, padding:'2px 7px',
                    }}>
                      <span style={{ color:'var(--text-primary)', fontWeight:600 }}>{m.ModuleName}</span>
                      <span style={{
                        fontSize:'0.62rem', fontWeight:800,
                        background:'rgba(245,158,11,.14)', color:'#92400e',
                        padding:'0 5px', borderRadius:8,
                      }}>{m.unmapped}</span>
                    </span>
                  ))}
                  {postImportCounts.length > 12 && (
                    <span style={{ fontSize:'0.69rem', color:'#94a3b8', padding:'2px 4px' }}>
                      +{postImportCounts.length - 12} more modules
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Auto-navigate countdown */}
            {navIn !== null && (
              <div style={{ background:'rgba(59,130,246,.05)', padding:'8px 16px',
                borderTop:'1px solid rgba(59,130,246,.12)',
                display:'flex', alignItems:'center', gap:8 }}>
                <ArrowRight size={12} color="#3b82f6" />
                <span style={{ fontSize:'0.73rem', color:'#3b82f6', fontWeight:600 }}>
                  Switching to Component Map in {navIn}s…
                </span>
                <button onClick={() => { setNavIn(null); onNavigate?.('compmap'); }}
                  data-testid="scantab-goto-compmap"
                  style={{ fontSize:'0.73rem', fontWeight:700, color:'#3b82f6',
                    background:'none', border:'1px solid rgba(59,130,246,.3)',
                    borderRadius:5, padding:'2px 8px', cursor:'pointer',
                    display:'flex', alignItems:'center', gap:3 }}>
                  Go now <ArrowRight size={10} />
                </button>
                <button onClick={() => setNavIn(null)}
                  style={{ fontSize:'0.72rem', color:'#94a3b8', background:'none',
                    border:'none', cursor:'pointer', marginLeft:2 }}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {/* Staged results panel */}
        {stagedItems.length > 0 && (
          <div>
            {/* Meta banner */}
            {importMeta && (
              <div style={{ fontSize:'0.72rem', color:'#64748b', marginBottom:10,
                background:'var(--surface-2,#f8fafc)', padding:'6px 10px', borderRadius:6,
                display:'flex', gap:12, flexWrap:'wrap' }}>
                <span><strong>{importMeta.filesScanned}</strong> files scanned</span>
                <span><strong>{importMeta.screensTotal}</strong> UAMS screens checked</span>
                <span><strong>{importMeta.screensMapped}</strong> matched (min {importMeta.minScore}%)</span>
                {importMeta.srcDir && <span style={{ color:'#94a3b8' }}>{importMeta.srcDir}</span>}
              </div>
            )}

            {/* Confidence filter + search */}
            <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
              <span style={{ fontSize:'0.72rem', color:'#64748b', fontWeight:600 }}>Filter:</span>
              {[
                { id:'all',    label:`All ${stagedItems.length}` },
                { id:'high',   label:`High ≥80% (${highCount})`,  color:'#22c55e' },
                { id:'medium', label:`Medium (${medCount})`,       color:'#f59e0b' },
                { id:'low',    label:`Low <50% (${lowCount})`,     color:'#ef4444' },
              ].map(f => (
                <button key={f.id} onClick={() => setConfFilter(f.id)}
                  style={{ ...pillBtn(confFilter === f.id), fontSize:'0.71rem',
                    color: confFilter === f.id ? '#fff' : (f.color || 'var(--text-secondary)') }}>
                  {f.label}
                </button>
              ))}
              <div style={{ flex:1 }} />
              <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                <Search size={11} color="#94a3b8" />
                <input value={searchQ} onChange={e => setSearchQ(e.target.value)}
                  placeholder="Search…"
                  data-testid="scantab-staged-search"
                  style={{ fontSize:'0.72rem', padding:'3px 7px', border:'1px solid var(--border)',
                    borderRadius:5, width:140 }} />
              </div>
            </div>

            {/* Quick-select row */}
            <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8, flexWrap:'wrap' }}>
              <span style={{ fontSize:'0.72rem', color:'#64748b' }}>
                {selectedIds.size} of {filteredItems.filter(s=>s.screenId).length} selected
              </span>
              <button onClick={selectHigh}
                style={{ fontSize:'0.69rem', background:'none', border:'1px solid #22c55e',
                  borderRadius:5, padding:'2px 8px', cursor:'pointer', color:'#16a34a' }}>
                Select High
              </button>
              <button onClick={() => selectAll(filteredItems.filter(s=>s.screenId))}
                style={{ fontSize:'0.69rem', background:'none', border:'1px solid var(--border)',
                  borderRadius:5, padding:'2px 8px', cursor:'pointer', color:'var(--text-secondary)' }}>
                Select All Visible
              </button>
              <button onClick={selectNone}
                style={{ fontSize:'0.69rem', background:'none', border:'1px solid var(--border)',
                  borderRadius:5, padding:'2px 8px', cursor:'pointer', color:'#ef4444' }}>
                Clear Selection
              </button>
              <div style={{ flex:1 }} />
              <button onClick={() => { setStagedItems([]); setSelectedIds(new Set()); setImportMeta(null); }}
                style={{ fontSize:'0.69rem', background:'none', border:'none',
                  cursor:'pointer', color:'#94a3b8', display:'flex', alignItems:'center', gap:3 }}>
                <X size={10} /> Dismiss
              </button>
            </div>

            {/* Staged table with pagination */}
            <div style={{ border:'1px solid var(--border)', borderRadius:8, overflow:'hidden', marginBottom:12 }}>
              <div style={{ overflowX:'auto', maxHeight:'38vh', overflowY:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.75rem' }}>
                  <thead style={{ position:'sticky', top:0, zIndex:2 }}>
                    <tr>
                      <th style={{ ...th, width:36 }}>
                        <input type="checkbox"
                          checked={filteredItems.filter(s=>s.screenId).every(s=>selectedIds.has(String(s.screenId)))}
                          onChange={e => e.target.checked
                            ? selectAll(filteredItems.filter(s=>s.screenId))
                            : selectNone()}
                          style={{ cursor:'pointer' }} />
                      </th>
                      <th style={th}>Screen ID</th>
                      <th style={th}>Screen Title</th>
                      <th style={th}>Component Path</th>
                      <th style={th}>Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.length === 0 ? (
                      <tr><td colSpan={5} style={{ padding:20, textAlign:'center', color:'#94a3b8' }}>
                        No items match the current filter
                      </td></tr>
                    ) : filteredItems.slice((stagedPage-1)*STAGED_PAGE_SIZE, stagedPage*STAGED_PAGE_SIZE).map((s, idx) => {
                      const hasId = !!s.screenId;
                      const checked = hasId && selectedIds.has(String(s.screenId));
                      return (
                        <tr key={s.screenId || idx}
                          onClick={() => hasId && toggleRow(String(s.screenId))}
                          onMouseEnter={e => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const x = Math.min(rect.left + 20, window.innerWidth - 460);
                            const y = rect.bottom + 4 > window.innerHeight - 140
                              ? rect.top - 148
                              : rect.bottom + 4;
                            setHoveredItem(s);
                            setTooltipPos({ x, y });
                          }}
                          onMouseLeave={() => setHoveredItem(null)}
                          style={{
                            borderBottom:'1px solid var(--border)',
                            background: checked ? 'rgba(59,130,246,.04)' : idx%2===0 ? 'transparent' : 'var(--surface-2,#f8fafc)',
                            cursor: hasId ? 'pointer' : 'default',
                            opacity: hasId ? 1 : 0.5,
                          }}>
                          <td style={{ padding:'5px 10px' }}>
                            {hasId && (
                              <input type="checkbox" checked={checked} readOnly
                                style={{ cursor:'pointer', pointerEvents:'none' }} />
                            )}
                          </td>
                          <td style={{ padding:'5px 10px', fontFamily:'monospace',
                            fontSize:'0.7rem', color:'#64748b', whiteSpace:'nowrap' }}>
                            {s.screenId || <span style={{ color:'#f59e0b', fontSize:'0.68rem' }}>no-id</span>}
                          </td>
                          <td style={{ padding:'5px 10px', maxWidth:180,
                            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                            color:'var(--text-primary)' }}>
                            {s._screenTitle || s.screenTitle || '—'}
                          </td>
                          <td style={{ padding:'5px 10px', fontFamily:'monospace',
                            fontSize:'0.69rem', color:'#475569', maxWidth:220,
                            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                            title={s.componentPath}>
                            {s.componentPath || s.componentName || '—'}
                          </td>
                          <td style={{ padding:'5px 10px' }}>
                            {s._confidence != null
                              ? <ConfidenceBadge score={s._confidence} />
                              : <span style={{ fontSize:'0.68rem', color:'#94a3b8' }}>unknown</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Pagination footer */}
              {filteredItems.length > STAGED_PAGE_SIZE && (
                <div style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 12px',
                  borderTop:'1px solid var(--border)', background:'var(--surface-2,#f8fafc)',
                  fontSize:'0.72rem', color:'#64748b' }}>
                  <span>
                    {(stagedPage-1)*STAGED_PAGE_SIZE+1}–{Math.min(stagedPage*STAGED_PAGE_SIZE, filteredItems.length)} of {filteredItems.length}
                  </span>
                  <div style={{ flex:1 }} />
                  <button onClick={() => setStagedPage(p => Math.max(1,p-1))}
                    disabled={stagedPage<=1}
                    style={{ padding:'2px 8px', border:'1px solid var(--border)', borderRadius:4,
                      cursor:stagedPage<=1?'default':'pointer', opacity:stagedPage<=1?.4:1,
                      background:'var(--surface)', fontSize:'0.72rem' }}>
                    ← Prev
                  </button>
                  <span style={{ fontWeight:700 }}>{stagedPage} / {Math.ceil(filteredItems.length/STAGED_PAGE_SIZE)}</span>
                  <button onClick={() => setStagedPage(p => Math.min(Math.ceil(filteredItems.length/STAGED_PAGE_SIZE),p+1))}
                    disabled={stagedPage>=Math.ceil(filteredItems.length/STAGED_PAGE_SIZE)}
                    style={{ padding:'2px 8px', border:'1px solid var(--border)', borderRadius:4,
                      cursor:stagedPage>=Math.ceil(filteredItems.length/STAGED_PAGE_SIZE)?'default':'pointer',
                      opacity:stagedPage>=Math.ceil(filteredItems.length/STAGED_PAGE_SIZE)?.4:1,
                      background:'var(--surface)', fontSize:'0.72rem' }}>
                    Next →
                  </button>
                </div>
              )}
            </div>

            {/* Progress bar (shows during accept for >=200 screens) */}
            {accepting && importProgress && (
              <div style={{ marginBottom:12 }}>
                <div style={{ height:6, borderRadius:3, background:'var(--surface-2,#e2e8f0)', overflow:'hidden', marginBottom:5 }}>
                  <div style={{
                    height:'100%', borderRadius:3,
                    background:'linear-gradient(90deg,#3b82f6,#22c55e)',
                    width: `${Math.round((importProgress.done/importProgress.total)*100)}%`,
                    transition:'width .3s ease',
                  }} />
                </div>
                <div style={{ fontSize:'0.72rem', color:'#64748b', display:'flex', gap:8 }}>
                  <span style={{ fontWeight:700 }}>
                    Batch {importProgress.batch}/{importProgress.batches}
                  </span>
                  <span>— applying {importProgress.done}–{Math.min(importProgress.done+IMPORT_CHUNK, importProgress.total)} of {importProgress.total} screens…</span>
                  <RefreshCw size={11} className="spin" style={{ marginLeft:'auto', color:'#3b82f6' }} />
                </div>
              </div>
            )}

            {/* Accept buttons */}
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {highCount > 0 && (
                <button className="erp-btn erp-btn-primary" onClick={acceptHigh}
                  disabled={accepting}
                  data-testid="scantab-bulk-accept-high"
                  style={{ fontSize:'0.78rem', display:'flex', alignItems:'center', gap:5,
                    background:'#22c55e', borderColor:'#22c55e' }}>
                  {accepting ? <RefreshCw size={12} className="spin" /> : <Zap size={12} />}
                  Bulk Accept High ({highCount})
                </button>
              )}
              <button className="erp-btn erp-btn-primary" onClick={acceptAllSelected}
                disabled={accepting || selectedIds.size === 0}
                data-testid="scantab-accept-selected"
                style={{ fontSize:'0.78rem', display:'flex', alignItems:'center', gap:5 }}>
                {accepting ? <RefreshCw size={12} className="spin" /> : <Check size={12} />}
                Accept Selected ({selectedIds.size})
              </button>
              <div style={{ display:'flex', alignItems:'center', gap:4, marginLeft:'auto',
                fontSize:'0.72rem', color:'#94a3b8' }}>
                <AlertTriangle size={11} />
                Items without screen ID ({stagedItems.filter(s=>!s.screenId).length}) will be fuzzy-matched
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Import History (session) ────────────────────────────── */}
      {importHistory.length > 0 && (
        <div style={{ background:'var(--surface,#fff)', border:'1px solid var(--border)', borderRadius:10 }}>
          <button onClick={() => setHistoryOpen(o => !o)}
            style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'10px 18px',
              background:'none', border:'none', cursor:'pointer', textAlign:'left' }}>
            <FileCode size={13} color="#64748b" />
            <span style={{ fontWeight:700, fontSize:'0.82rem', color:'var(--text-secondary)' }}>
              Import History
            </span>
            <span style={{ fontSize:'0.7rem', color:'#94a3b8', background:'var(--surface-2,#f1f5f9)',
              padding:'1px 8px', borderRadius:10 }}>
              {importHistory.length} session import{importHistory.length > 1 ? 's' : ''}
            </span>
            <div style={{ flex:1 }} />
            {historyOpen ? <ChevronUp size={13} color="#94a3b8" /> : <ChevronDown size={13} color="#94a3b8" />}
          </button>
          {historyOpen && (
            <div style={{ padding:'0 18px 14px', borderTop:'1px solid var(--border)' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.73rem', marginTop:10 }}>
                <thead>
                  <tr style={{ background:'var(--surface-2,#f8fafc)' }}>
                    {['Time','Source','Sent','Applied','Direct','Fuzzy','Failed'].map(h => (
                      <th key={h} style={{ padding:'4px 10px', textAlign:'left', fontWeight:700,
                        color:'#64748b', fontSize:'0.67rem', textTransform:'uppercase',
                        letterSpacing:'.04em', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {importHistory.map((h, i) => (
                    <tr key={i} style={{ borderBottom:'1px solid var(--border)' }}>
                      <td style={{ padding:'5px 10px', color:'#94a3b8', whiteSpace:'nowrap' }}>
                        {new Date(h.timestamp).toLocaleTimeString()}
                      </td>
                      <td style={{ padding:'5px 10px', fontFamily:'monospace', fontSize:'0.69rem',
                        color:'#475569', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                        title={h.srcDir}>
                        {h.srcDir || '—'}
                      </td>
                      <td style={{ padding:'5px 10px', fontWeight:600 }}>{h.total}</td>
                      <td style={{ padding:'5px 10px', fontWeight:700, color:'#16a34a' }}>{h.applied}</td>
                      <td style={{ padding:'5px 10px', color:'#3b82f6' }}>{h.direct}</td>
                      <td style={{ padding:'5px 10px', color:'#8b5cf6' }}>{h.matched}</td>
                      <td style={{ padding:'5px 10px', color: h.failed > 0 ? '#ef4444' : '#94a3b8' }}>{h.failed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── STEP 3: Code Snippets ───────────────────────────────── */}
      <div style={{ background:'var(--surface,#fff)', border:'1px solid var(--border)',
        borderRadius:10, padding:'18px 20px' }}>
        {stepHead(3, <Code2 size={14} color="#22c55e" />, 'Generate Code Snippets',
          'After mapping, generate ready-to-use code for your ERP project.')}

        <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
          <div style={{ flex:'1 1 260px', padding:'12px 14px',
            background:'var(--surface-2,#f8fafc)', border:'1px solid var(--border)', borderRadius:8 }}>
            <div style={{ fontWeight:700, fontSize:'0.8rem', marginBottom:4, display:'flex', alignItems:'center', gap:5 }}>
              <FileCode size={13} color="#3b82f6" /> menu.config.js
            </div>
            <div style={{ fontSize:'0.73rem', color:'#64748b', marginBottom:10, lineHeight:1.5 }}>
              Full module → sub-module → screen navigation tree.
              Use as your sidebar / router config.
            </div>
          </div>
          <div style={{ flex:'1 1 260px', padding:'12px 14px',
            background:'var(--surface-2,#f8fafc)', border:'1px solid var(--border)', borderRadius:8 }}>
            <div style={{ fontWeight:700, fontSize:'0.8rem', marginBottom:4, display:'flex', alignItems:'center', gap:5 }}>
              <FileCode size={13} color="#8b5cf6" /> screens/index.js
            </div>
            <div style={{ fontSize:'0.73rem', color:'#64748b', marginBottom:10, lineHeight:1.5 }}>
              Lazy-import barrel: <code>screenRegistry['SCR001']</code> → dynamic import.
              Drop into your app for code-split navigation.
            </div>
          </div>
        </div>

        <div style={{ marginTop:14 }}>
          <button className="erp-btn erp-btn-primary" onClick={() => setShowSnippets(true)}
            data-testid="scantab-gen-snippets-btn"
            style={{ fontSize:'0.8rem', padding:'8px 18px', display:'flex', alignItems:'center', gap:6 }}>
            <Code2 size={13} /> Generate &amp; Download Snippets
          </button>
        </div>
      </div>

      {/* ── Server Auto-Scan (collapsible) ──────────────────────── */}
      <div style={{ background:'var(--surface,#fff)', border:'1px solid var(--border)', borderRadius:10 }}>
        <button
          onClick={() => setAutoOpen(o => !o)}
          style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'12px 18px',
            background:'none', border:'none', cursor:'pointer', textAlign:'left' }}>
          <Cpu size={13} color="#94a3b8" />
          <span style={{ fontWeight:700, fontSize:'0.82rem', color:'var(--text-secondary)' }}>
            Server Auto-Scan
          </span>
          <span style={{ fontSize:'0.7rem', color:'#94a3b8', background:'var(--surface-2,#f1f5f9)',
            padding:'1px 8px', borderRadius:10 }}>
            Scans this admin app's own frontend files
          </span>
          <div style={{ flex:1 }} />
          {autoOpen ? <ChevronUp size={13} color="#94a3b8" /> : <ChevronDown size={13} color="#94a3b8" />}
        </button>

        {autoOpen && (
          <div style={{ padding:'0 18px 18px', borderTop:'1px solid var(--border)' }}>
            <div style={{ paddingTop:14, display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', marginBottom:12 }}>
              <button className="erp-btn erp-btn-primary" onClick={runAutoScan}
                disabled={scanning || bulkApplying}
                data-testid="erpws-scan-btn"
                style={{ display:'flex', alignItems:'center', gap:5, fontSize:'0.78rem' }}>
                {scanning ? <><RefreshCw size={12} className="spin" /> Scanning…</> : <><Cpu size={12} /> Run Auto-Scan</>}
              </button>
              {scanResult && scanResult.suggestions.filter(s=>s.confidence==='high').length > 0 && (
                <button className="erp-btn erp-btn-primary"
                  onClick={() => applyAutoBulk('high')}
                  disabled={bulkApplying||scanning}
                  data-testid="erpws-bulk-apply-high"
                  style={{ display:'flex', alignItems:'center', gap:5, fontSize:'0.78rem',
                    background:'#22c55e', borderColor:'#22c55e' }}>
                  {bulkApplying ? <RefreshCw size={12} className="spin" /> : <Zap size={12} />}
                  Apply All High ({scanResult.suggestions.filter(s=>s.confidence==='high').length})
                </button>
              )}
              {scanResult && (
                <>
                  <select value={autoFilter} onChange={e => setAutoFilter(e.target.value)}
                    style={{ fontSize:'0.75rem', padding:'4px 8px', border:'1px solid var(--border)', borderRadius:5 }}>
                    <option value="">All Confidence</option>
                    <option value="high">High only</option>
                    <option value="medium">Medium only</option>
                    <option value="none">Unmatched</option>
                    <option value="applied">Applied</option>
                  </select>
                  <div style={{ position:'relative' }}>
                    <Search size={12} style={{ position:'absolute', left:7, top:'50%', transform:'translateY(-50%)', color:'#94a3b8' }} />
                    <input value={autoSearch} onChange={e => setAutoSearch(e.target.value)}
                      placeholder="Filter…"
                      style={{ paddingLeft:24, fontSize:'0.75rem', padding:'4px 4px 4px 24px',
                        border:'1px solid var(--border)', borderRadius:5, width:160 }} />
                  </div>
                  <span style={{ marginLeft:'auto', fontSize:'0.72rem', color:'#94a3b8' }}>
                    {scanResult.components} files · {scanResult.suggestions.filter(s=>s.confidence==='high').length} high · {scanResult.suggestions.filter(s=>s.confidence==='medium').length} medium
                  </span>
                </>
              )}
            </div>

            {!scanResult && !scanning && (
              <div style={{ textAlign:'center', padding:'24px 0', color:'#94a3b8', fontSize:'0.82rem' }}>
                Click "Run Auto-Scan" to analyse this app's frontend files for component mapping suggestions.
              </div>
            )}

            {scanResult && (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.75rem' }}>
                  <thead>
                    <tr style={{ background:'var(--surface-2,#f8fafc)', borderBottom:'2px solid var(--border)' }}>
                      {['Screen ID','Screen Title','Suggested Component','Path','Conf.',''].map(h=>(
                        <th key={h} style={th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {autoFiltered.filter(s=>s.suggestedName&&s.confidence!=='applied').map(s => (
                      <tr key={s.screenId} style={{ borderBottom:'1px solid var(--border)' }}>
                        <td style={{ padding:'5px 10px', fontFamily:'monospace', fontSize:'0.71rem', color:'#94a3b8' }}>{s.screenId}</td>
                        <td style={{ padding:'5px 10px', maxWidth:150, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.screenTitle}</td>
                        <td style={{ padding:'5px 10px', fontFamily:'monospace', color:'#3b82f6', fontSize:'0.71rem' }}>{s.suggestedName}</td>
                        <td style={{ padding:'5px 10px', fontFamily:'monospace', color:'#94a3b8', fontSize:'0.71rem', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.suggestedPath}</td>
                        <td style={{ padding:'5px 10px' }}>
                          <span style={{ fontSize:'0.68rem', fontWeight:700, padding:'1px 6px', borderRadius:4,
                            background: s.confidence==='high' ? 'rgba(34,197,94,.1)' : 'rgba(245,158,11,.1)',
                            color: s.confidence==='high' ? '#16a34a' : '#b45309' }}>
                            {s.confidence}
                          </span>
                        </td>
                        <td style={{ padding:'5px 10px' }}>
                          <button className="erp-btn erp-btn-primary"
                            onClick={() => applyAutoSuggestion(s)}
                            disabled={applying.has(s.screenId)||bulkApplying}
                            data-testid={`scan-apply-${s.screenId}`}
                            style={{ fontSize:'0.7rem', padding:'2px 8px' }}>
                            {applying.has(s.screenId) ? 'Saving…' : 'Apply'}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {autoFiltered.filter(s=>s.confidence==='applied').map(s => (
                      <tr key={s.screenId+'_applied'} style={{ borderBottom:'1px solid var(--border)', opacity:0.6 }}>
                        <td style={{ padding:'4px 10px', fontFamily:'monospace', fontSize:'0.71rem', color:'#94a3b8' }}>{s.screenId}</td>
                        <td style={{ padding:'4px 10px', color:'var(--text-secondary)' }}>{s.screenTitle}</td>
                        <td style={{ padding:'4px 10px', fontFamily:'monospace', color:'#3b82f6', fontSize:'0.71rem' }} colSpan={2}>{s.suggestedName}</td>
                        <td colSpan={2} style={{ padding:'4px 10px' }}>
                          <span style={{ color:'#22c55e', fontSize:'0.7rem', fontWeight:600 }}>
                            <Check size={11} style={{ verticalAlign:'middle', marginRight:3 }}/>Applied
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Code Snippets Modal ──────────────────────────────────── */}
      {showSnippets && <CodeSnippetsModal onClose={() => setShowSnippets(false)} />}

      {/* ── Snippet Tooltip ──────────────────────────────────────── */}
      <SnippetTooltip item={hoveredItem} pos={tooltipPos} />
    </div>
  );
}
